// The pitches the player sees before a move: moves the search found, shown
// on the board and in the cue. The player may take one, or move another
// piece. Rob, 2026-10-13: "instead of getting one OK pitch from the engine,
// the player will get one OK pitch and one *highly aggressive* pitch. But
// we'll pin each of these pitches to a knight", and "if one of these knights
// is captured, the player loses that pitch".
//   careful  From a piece of the player that gives the careful pitch. The
//            usual score, to the depth of a pitch.
//   aggro    From a piece that gives the aggro pitch. The aggro score, to
//            the same depth. When it is the same move as the careful one,
//            that piece offers a move of its own.
//   deep     From a cast of suggest-best: the long search. It is the one
//            pitch that looks deeper, so it is worth more than the others.
// In a match with a wheel no piece gives a pitch, and the player gets the
// careful one as a hint. The searches run once for a board.

import { requestSearch } from '../ai/client';
import { bestOwnAggro, isSameMove, ownAggroRequests } from '../ai/pitch';
import { AI_HINT_DEPTH, AI_SUGGEST_BUDGET_MS, AI_TIME_BUDGET_MS } from '../constants';
import { livingPitches } from '../engine/roles';
import type { Board, Move, Pitch } from '../engine/types';
import type { GameState } from '../game/state';
import { createStore } from '../lib/store';
import { gameStore } from './game';
import { actsFor, localSideStore } from './localSide';

/** A source of a pitch, in the order the cue shows them. */
export type PitchKind = Pitch | 'deep';
export const PITCH_KINDS: readonly PitchKind[] = ['careful', 'aggro', 'deep'];

/** The move of each pitch the player has now. A kind with no move is missing, or its search still runs. */
export type Pitches = Readonly<Partial<Record<PitchKind, Move>>>;

const NO_PITCHES: Pitches = {};
export const suggestionStore = createStore<Pitches>(NO_PITCHES);

/** The board and the kinds the searches ran for, or run for now. */
let current: { board: Board; kinds: string } | null = null;

/** The pitches the player has for the move: one for each kind a living piece gives, and the deep one after suggest-best. */
const wantedKinds = (state: GameState): PitchKind[] => {
  if (!actsFor(state.turn) || state.phase !== 'move') return [];
  const living = livingPitches(state.board, state.turn);
  const fromPieces: PitchKind[] =
    state.mode === 'gods'
      ? (['careful', 'aggro'] as const).filter((kind) => living.has(kind))
      : ['careful'];
  return state.suggestFor === state.turn ? [...fromPieces, 'deep'] : fromPieces;
};

const searchFor = (state: GameState, kind: PitchKind): Promise<Move | null> =>
  requestSearch({
    board: state.board,
    rules: state.rules,
    side: state.turn,
    onlyPieceId: state.mustMovePieceId,
    budgetMs: kind === 'deep' ? AI_SUGGEST_BUDGET_MS : AI_TIME_BUDGET_MS,
    style: kind === 'aggro' ? 'aggro' : 'careful',
    ...(kind === 'deep' ? {} : { maxDepth: AI_HINT_DEPTH }),
  }).then((result) => result.move);

const consider = (state: GameState): void => {
  const kinds = wantedKinds(state);
  if (kinds.length === 0) {
    current = null;
    if (suggestionStore.get() !== NO_PITCHES) suggestionStore.set(NO_PITCHES);
    return;
  }
  // One set of searches for a board. A cast of suggest-best leaves the board as it is and adds a kind.
  const key = kinds.join();
  if (current?.board === state.board && current.kinds === key) return;
  const search = { board: state.board, kinds: key };
  current = search;
  suggestionStore.set(NO_PITCHES);
  // The game moved on while a search ran.
  const isCurrent = (): boolean => current === search;
  const show = (kind: PitchKind, move: Move | null): void => {
    if (isCurrent() && move) suggestionStore.set({ ...suggestionStore.get(), [kind]: move });
  };
  const careful = kinds.includes('careful') ? searchFor(state, 'careful') : null;
  void careful?.then((move) => show('careful', move));
  if (kinds.includes('aggro')) {
    void Promise.all([careful, searchFor(state, 'aggro')]).then(async ([carefulMove, aggro]) => {
      // The two pitches are the same move. The piece that gives the aggro pitch offers
      // a move of its own. With none, the two stay the same.
      if (!isSameMove(carefulMove, aggro) || !aggro || !isCurrent()) return show('aggro', aggro);
      const results = await Promise.all(
        ownAggroRequests(state, state.turn, aggro).map(requestSearch),
      );
      return show('aggro', bestOwnAggro(results) ?? aggro);
    });
  }
  if (kinds.includes('deep')) void searchFor(state, 'deep').then((move) => show('deep', move));
};

/** Call once at startup. */
export const startSuggestion = (): void => {
  gameStore.subscribe(consider);
  localSideStore.subscribe(() => consider(gameStore.get()));
  consider(gameStore.get());
};
